import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";

const featured = movies.filter((movie) => movie.featured);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % featured.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, []);

  const active = featured[index];

  const goTo = (next: number) => {
    setIndex((next + featured.length) % featured.length);
  };

  return (
    <section className="relative overflow-hidden bg-primary-950">
      {featured.map((movie, position) => (
        <div
          key={movie.id}
          aria-hidden={position !== index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            position === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 场景插画`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/85 to-primary-950/35"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/40"></div>
        </div>
      ))}

      <div className="relative mx-auto grid max-w-page grid-cols-1 items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:min-h-[620px] lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
        <div className="animate-fade-up">
          <p className="flex items-center gap-2 font-label text-[12px] uppercase tracking-[0.28em] text-accent-300">
            <span className="inline-block h-[1px] w-8 bg-accent-400"></span>
            1980 — 1989 · 电影档案
          </p>
          <h1 className="mt-5 font-heading text-5xl font-semibold leading-[1.05] text-background-50 md:text-6xl lg:text-7xl">
            80s电影
            <span className="mt-4 block font-heading text-xl font-normal leading-snug text-background-100/85 md:text-2xl">
              重返八十年代的光影现场
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-background-200/85 md:text-base">
            从《回到未来》的时间悖论，到《银翼杀手》的雨夜霓虹；从《死亡诗社》的课桌，到《早餐俱乐部》的图书馆。这里收录那个年代最值得反复观看的影片、影评与资讯。
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/category"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
            >
              浏览电影分类
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/30 px-6 py-3 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-200"
            >
              阅读影评资讯
              <i className="ri-book-open-line"></i>
            </Link>
          </div>
        </div>

        <div className="w-full">
          <div className="rounded-lg border border-background-100/15 bg-primary-950/45 p-4 backdrop-blur-md md:p-5">
            <div className="flex items-center justify-between">
              <p className="font-label text-[11px] uppercase tracking-[0.26em] text-accent-300">
                本期焦点 · 0{index + 1}
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  aria-label="上一部焦点影片"
                  onClick={() => goTo(index - 1)}
                  className="flex h-7 w-7 items-center justify-center rounded-full border border-background-100/25 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-200"
                >
                  <i className="ri-arrow-left-s-line text-sm"></i>
                </button>
                <button
                  type="button"
                  aria-label="下一部焦点影片"
                  onClick={() => goTo(index + 1)}
                  className="flex h-7 w-7 items-center justify-center rounded-full border border-background-100/25 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-200"
                >
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </button>
              </div>
            </div>

            <Link to={`/movie/${active.id}`} className="group mt-4 flex gap-4 md:gap-5">
              <div className="h-40 w-28 shrink-0 overflow-hidden rounded-md bg-secondary-800 md:h-48 md:w-36">
                <img
                  src={active.poster}
                  alt={`${active.title} 海报`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="flex min-w-0 flex-col justify-center">
                <p className="font-label text-[11px] uppercase tracking-[0.2em] text-accent-300">
                  {active.originalTitle}
                </p>
                <h2 className="mt-1.5 font-heading text-2xl font-semibold text-background-50 md:text-3xl">
                  {active.title}
                </h2>
                <p className="mt-2 text-xs text-background-200/80">
                  {active.year} · {active.genres.join(" / ")} · {active.runtime} 分钟
                </p>
                <p className="mt-3 line-clamp-3 text-xs leading-relaxed text-background-200/85 md:text-sm">
                  {active.tagline}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs text-accent-200">
                  进入影片详情
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </Link>
          </div>

          <div className="mt-4 flex items-center gap-2">
            {featured.map((movie, position) => (
              <button
                key={movie.id}
                type="button"
                aria-label={`切换到${movie.title}`}
                onClick={() => setIndex(position)}
                className={`h-1 rounded-full transition-all ${
                  position === index ? "w-10 bg-accent-500" : "w-5 bg-background-100/30"
                }`}
              ></button>
            ))}
            <span className="ml-auto font-label text-[11px] tracking-[0.2em] text-background-300/70">
              {String(index + 1).padStart(2, "0")} / {String(featured.length).padStart(2, "0")}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}